import { useEffect, useState } from 'react';
import { orderApi } from './order.api';
import type { OrderSearchQuery } from './order.types';

/**
 * 동일 전화번호로 여러 번 주문했는지 확인하는 hook
 *
 * @param phoneNumber 전화번호 (숫자만 포함된 문자열 또는 null/undefined)
 * @param currentOrderId 현재 주문 ID (자기 자신은 제외하기 위해)
 * @returns { hasMultipleOrders: boolean, isLoading: boolean }
 */
export function useHasMultipleOrdersByPhone(
  phoneNumber: string | null | undefined,
  currentOrderId?: string
) {
  const [hasMultipleOrders, setHasMultipleOrders] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    // 전화번호가 없으면 false
    if (!phoneNumber) {
      setHasMultipleOrders(false);
      setIsLoading(false);
      return;
    }

    // 전화번호에서 숫자만 추출
    const digitsOnly = phoneNumber.replace(/\D/g, '');
    if (!digitsOnly) {
      setHasMultipleOrders(false);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);

    // 전화번호로 주문 검색
    const query: OrderSearchQuery = {
      filterGroups: [
        {
          logic: 'AND',
          filters: [
            {
              field: 'ordererPhone',
              operator: 'EQUALS',
              value: digitsOnly,
              values: null,
            },
          ],
        },
      ],
      sortBy: 'orderedAt',
      sortOrder: 'DESC',
      pageSize: 10, // 최소 2개만 확인하면 되므로 10개면 충분
    };

    orderApi
      .searchOrders(query)
      .then((result) => {
        // 현재 주문을 제외한 주문 개수 확인
        const otherOrders = currentOrderId
          ? result.items.filter((order) => order.id !== currentOrderId)
          : result.items;

        // 다른 주문 1개라도 있으면 중복 주문으로 판단
        setHasMultipleOrders(otherOrders.length >= 1);
      })
      .catch(() => {
        // 에러 발생 시 false로 설정 (경고를 표시하지 않음)
        setHasMultipleOrders(false);
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [phoneNumber, currentOrderId]);

  return {
    hasMultipleOrders,
    isLoading,
  };
}
